/**
 * FileUploadWidget Component
 * 
 * A reusable component for handling file selection with different states:
 * - idle: Shows a drop zone or file selector
 * - selected: Shows selected file with name and remove option
 * - error: Shows error message with retry option
 */

import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Icon,
  Image,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUpload, FiX, FiFile, FiCheck, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';

/**
 * FileUploadWidget component
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the upload field
 * @param {string} props.fileType - Type of file (e.g., 'personalPhoto', 'cv', 'securityClearance')
 * @param {string} props.acceptedFileTypes - MIME types accepted (e.g., 'image/*,.pdf')
 * @param {Function} props.onFileSelect - Callback when file is selected
 * @param {string} props.initialFileUrl - Initial file URL if already uploaded
 * @param {string} props.helperText - Helper text to display below the upload area
 * @param {string} props.errorMessage - Error message to display if validation fails
 * @param {boolean} props.isRequired - Whether the field is required
 * @param {boolean} props.isInvalid - Whether the field is invalid
 */
const FileUploadWidget = ({
  label,
  fileType,
  acceptedFileTypes,
  onFileSelect,
  initialFileUrl = null,
  helperText,
  errorMessage,
  isRequired = false,
  isInvalid = false,
}) => {
  const inputId = `file-input-${fileType}`;
  const isSamsung = typeof navigator !== 'undefined' && /SamsungBrowser/i.test(navigator.userAgent);
  const isImageField = (acceptedFileTypes || '').includes('image');
  // For Samsung Internet: if not an image field, clear accept to force full file chooser
  const effectiveAccept = (isSamsung && !isImageField) ? '' : acceptedFileTypes;
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(initialFileUrl);
  const [status, setStatus] = useState(initialFileUrl ? 'selected' : 'idle');
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');
  // Frontend file size limit (must match backend): 25 MB
  const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
  const MAX_FILE_SIZE_LABEL = '25MB';

  // Colors for different states
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const activeBorderColor = useColorModeValue('primary.500', 'primary.300');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const activeBgColor = useColorModeValue('gray.100', 'gray.600');

  // Build helper text from acceptedFileTypes to avoid mismatches
  const getAcceptedHint = () => {
    if (!acceptedFileTypes) return '';
    const lower = acceptedFileTypes.toLowerCase();
    // If PDF is among accepted types, show a simple, user-friendly label
    if (lower.includes('application/pdf') || lower.includes('application/x-pdf') || lower.includes('.pdf')) {
      return `الصيغة المطلوبة: PDF — الحد الأقصى للحجم: ${MAX_FILE_SIZE_LABEL}`;
    }
    if (acceptedFileTypes === 'image/*') {
      return `الصيغ المدعومة: JPG, PNG, GIF — الحد الأقصى للحجم: ${MAX_FILE_SIZE_LABEL}`;
    }
    // Split by comma and map to friendly labels
    const tokens = acceptedFileTypes.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    const labelFor = (t) => {
      if (t === '.pdf' || t === 'application/pdf') return 'PDF';
      if (t === '.doc' || t === 'application/msword') return 'DOC';
      if (t === '.docx' || t === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
      if (t.startsWith('image/')) return 'صور';
      if (t.startsWith('.')) return t.slice(1).toUpperCase();
      return t.toUpperCase();
    };
    const labels = Array.from(new Set(tokens.map(labelFor)));
    return `الصيغ المدعومة: ${labels.join(', ')} — الحد الأقصى للحجم: ${MAX_FILE_SIZE_LABEL}`;
  };

  // Initialize with initial file if provided
  React.useEffect(() => {
    if (initialFileUrl) {
      setPreviewUrl(initialFileUrl);
    }
  }, [initialFileUrl]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      // Enforce size limit
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setStatus('error');
        setError(`الحد الأقصى لحجم الملف ${MAX_FILE_SIZE_LABEL}`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setFileName(file.name);
      
      // Create preview URL for images
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      }
      
      // Handle PDF files - some mobile devices might report different MIME types
      const isPDF = 
        file.type === 'application/pdf' || 
        file.type === 'application/x-pdf' || 
        file.name.toLowerCase().endsWith('.pdf');
      
      // For PDF files on mobile, ensure we handle them properly
      if (isPDF && !file.type.startsWith('application/')) {
        console.log('PDF file detected with non-standard MIME type:', file.type);
        // Force the correct type for better handling
        Object.defineProperty(file, 'type', {
          writable: true,
          value: 'application/pdf'
        });
      }
      // If this field requires documents only (PDF), enforce client-side validation
      if ((fileType === 'cv' || fileType === 'security_clearance') && !isPDF) {
        setStatus('error');
        setError('يرجى اختيار ملف PDF فقط');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      
      setStatus('selected');
      
      // Call the callback with the file
      if (onFileSelect) {
        onFileSelect(file, fileType);
      }
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Failed to select file');
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      try {
        // Enforce size limit
        if (file.size > MAX_FILE_SIZE_BYTES) {
          setStatus('error');
          setError(`الحد الأقصى لحجم الملف ${MAX_FILE_SIZE_LABEL}`);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        setFileName(file.name);
        
        // Create preview URL for images
        if (file.type.startsWith('image/')) {
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
        }
        
        // Handle PDF files - some mobile devices might report different MIME types
        const isPDF = 
          file.type === 'application/pdf' || 
          file.type === 'application/x-pdf' || 
          file.name.toLowerCase().endsWith('.pdf');
        
        // For PDF files on mobile, ensure we handle them properly
        if (isPDF && !file.type.startsWith('application/')) {
          console.log('PDF file detected with non-standard MIME type:', file.type);
          // Force the correct type for better handling
          Object.defineProperty(file, 'type', {
            writable: true,
            value: 'application/pdf'
          });
        }
        // If this field requires documents only (PDF), enforce client-side validation
        if ((fileType === 'cv' || fileType === 'security_clearance') && !isPDF) {
          setStatus('error');
          setError('يرجى اختيار ملف PDF فقط');
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        
        setStatus('selected');
        
        // Call the callback with the file
        if (onFileSelect) {
          onFileSelect(file, fileType);
        }
      } catch (err) {
        setStatus('error');
        setError(err.message || 'Failed to select file');
      }
    }
  };

  // Handle remove file
  const handleRemove = () => {
    setStatus('idle');
    setPreviewUrl(null);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onFileSelect) {
      onFileSelect(null, fileType);
    }
  };

  // Handle retry file selection
  const handleRetry = () => {
    setStatus('idle');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Render different UI based on status
  const renderContent = () => {
    switch (status) {
      case 'selected':
        return (
          <Box p={4} textAlign="center">
            {previewUrl && previewUrl.match(/\.(jpeg|jpg|gif|png)$/i) ? (
              <Image 
                src={previewUrl} 
                alt={fileName} 
                maxH="100px" 
                mx="auto" 
                mb={2} 
                borderRadius="md"
              />
            ) : (
              <Icon as={FiFile} boxSize={10} color="primary.500" mb={2} />
            )}
            <Flex align="center" justify="center" mb={2}>
              <Icon as={FiCheck} color="green.500" mr={2} />
              <Text fontWeight="medium" isTruncated>{fileName}</Text>
            </Flex>
            <Button 
              size="sm" 
              leftIcon={<FiX />} 
              variant="outline" 
              colorScheme="red" 
              onClick={handleRemove}
            >
              Remove
            </Button>
          </Box>
        );
        
      case 'error':
        return (
          <Box p={4} textAlign="center">
            <Icon as={FiAlertCircle} boxSize={10} color="red.500" mb={2} />
            <Text color="red.500" mb={2}>{error || 'File selection failed'}</Text>
            <Button 
              size="sm" 
              leftIcon={<FiRefreshCw />} 
              colorScheme="primary" 
              onClick={handleRetry}
            >
              Retry
            </Button>
          </Box>
        );
        
      default: // idle
        return (
          <Box
            p={4}
            textAlign="center"
            cursor="pointer"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            bg={dragActive ? activeBgColor : bgColor}
            borderWidth={2}
            borderStyle="dashed"
            borderColor={dragActive ? activeBorderColor : borderColor}
            borderRadius="md"
            transition="all 0.2s"
            _hover={{ bg: activeBgColor }}
          >
            <input
              id={inputId}
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept={effectiveAccept}
              style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
              capture={false}
              multiple={false}
            />
            <Icon as={FiUpload} boxSize={8} color="gray.400" mb={2} />
            <Text fontWeight="medium">انقر أو اسحب الملف لإختياره</Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {getAcceptedHint()}
            </Text>
          </Box>
        );
    }
  };

  return (
    <FormControl isRequired={isRequired} isInvalid={isInvalid} mb={4}>
      <FormLabel>{label}</FormLabel>
      {renderContent()}
      {helperText && !isInvalid && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
      {isInvalid && errorMessage && (
        <FormErrorMessage>{errorMessage}</FormErrorMessage>
      )}
    </FormControl>
  );
};

export default FileUploadWidget;
